import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { generateUuid } from '../components/Calendar/Events/EventsUtils';
import { firestoreService } from '../services/firestore.service';
import { cacheService } from '../services/cache.service';
import { createDebouncedSync, syncImmediate, cancelSync, cancelAllSyncs, SYNC_TIMING } from '../services/syncHelper';

export const useTasksStore = create(
    persist(
        (set, get) => ({
            taskLists: [],
            userId: null, // Set when user logs in
            isInitialized: false,
            isSyncing: false,

            // ===== FIRESTORE SYNC (GRO-46) =====

            /**
             * Initialize store with user ID and load from Firestore
             * @param {string} userId - Firebase user ID
             * @param {boolean} forceRefresh - Skip cache and fetch from Firestore
             */
            initializeWithUser: async (userId, forceRefresh = false) => {
                set({ userId, isSyncing: true });

                try {
                    // Load from Firestore (with cache)
                    const taskLists = await firestoreService.getTaskLists(userId, !forceRefresh);

                    set({
                        taskLists: taskLists.length > 0 ? taskLists : get().taskLists,
                        isInitialized: true,
                        isSyncing: false
                    });
                } catch (error) {
                    console.error('Failed to initialize tasks from Firestore:', error);
                    set({ isInitialized: true, isSyncing: false });
                }
            },

            /**
             * Sync a task list to Firestore (debounced)
             */
            syncTaskList: (taskListId) => {
                const { userId, taskLists } = get();
                if (!userId) return;

                const taskList = taskLists.find(l => l.id === taskListId);
                if (taskList) {
                    createDebouncedSync(
                        `task-${taskListId}`,
                        () => firestoreService.updateTaskList(userId, taskListId, taskList),
                        () => cacheService.updateTaskList(taskListId, taskList),
                        SYNC_TIMING.NORMAL
                    )();
                }
            },

            /**
             * Force sync all task lists to Firestore
             */
            syncAllToFirestore: async () => {
                const { userId, taskLists } = get();
                if (!userId) return;

                set({ isSyncing: true });
                try {
                    for (const taskList of taskLists) {
                        await firestoreService.updateTaskList(userId, taskList.id, taskList);
                    }
                } catch (error) {
                    console.error('Failed to sync all tasks to Firestore:', error);
                } finally {
                    set({ isSyncing: false });
                }
            },

            /**
             * Clear user data on logout
             */
            clearUserData: () => {
                // Cancel all pending task syncs
                get().taskLists.forEach(list => cancelSync(`task-${list.id}`));
                cacheService.remove('grownups-tasks-cache');
                set({ userId: null, isInitialized: false });
            },

            // Get list names by ID (computed)
            getListsNamesByID: () => {
                const listsNames = {};
                get().taskLists.forEach(list => {
                    listsNames[list.id] = list.title;
                });
                return listsNames;
            },

            // ===== LIST OPERATIONS =====

            createTaskList: (title = '') => ({
                id: generateUuid(),
                title,
                created: Date.now(),
                tasks: [],
                scheduledtasks: [],
                completedtasks: []
            }),

            addTaskList: async (taskList) => {
                set((state) => ({
                    taskLists: [...state.taskLists, taskList]
                }));
                // Sync to Firestore immediately (create operation)
                const { userId } = get();
                if (userId) {
                    await syncImmediate(
                        `task-create-${taskList.id}`,
                        () => firestoreService.createTaskList(userId, taskList),
                        () => cacheService.updateTaskList(taskList.id, taskList)
                    );
                }
            },

            removeTaskList: async (taskListId) => {
                // Cancel any pending sync for this task list
                cancelSync(`task-${taskListId}`);

                set((state) => ({
                    taskLists: state.taskLists.filter(list => list.id !== taskListId)
                }));
                // Delete from Firestore immediately
                const { userId } = get();
                if (userId) {
                    await syncImmediate(
                        `task-delete-${taskListId}`,
                        () => firestoreService.deleteTaskList(userId, taskListId),
                        () => cacheService.removeTaskList(taskListId)
                    );
                }
            },

            updateTaskList: (listId, updates) => {
                set((state) => ({
                    taskLists: state.taskLists.map(list =>
                        list.id === listId ? { ...list, ...updates } : list
                    )
                }));
                // Sync to Firestore (debounced)
                get().syncTaskList(listId);
            },

            setListTasks: (listId, tasks) => {
                get().updateTaskList(listId, { tasks });
            },

            updateListColors: (listId, color) => {
                get().updateTaskList(listId, { color });
            },

            updateListTitle: (listId, title) => {
                get().updateTaskList(listId, { title });
            },

            // ===== TASK OPERATIONS =====

            createTask: (title = '') => ({
                id: generateUuid(),
                title,
                created: Date.now()
            }),

            addTask: (listId, task) => {
                set((state) => ({
                    taskLists: state.taskLists.map(list =>
                        list.id === listId
                            ? {
                                ...list,
                                tasks: [...list.tasks.filter(t => t.id !== task.id), task],
                                scheduledtasks: list.scheduledtasks.filter(t => t.id !== task.id),
                                completedtasks: list.completedtasks.filter(t => t.id !== task.id)
                            }
                            : list
                    )
                }));
                get().syncTaskList(listId);
            },

            removeTask: (listId, taskId) => {
                set((state) => ({
                    taskLists: state.taskLists.map(list =>
                        list.id === listId
                            ? { ...list, tasks: list.tasks.filter(t => t.id !== taskId) }
                            : list
                    )
                }));
                get().syncTaskList(listId);
            },

            updateTask: (listId, taskId, updates) => {
                set((state) => ({
                    taskLists: state.taskLists.map(list =>
                        list.id === listId
                            ? {
                                ...list,
                                tasks: list.tasks.map(t =>
                                    t.id === taskId ? { ...t, ...updates } : t
                                )
                            }
                            : list
                    )
                }));
                get().syncTaskList(listId);
            },

            completeTask: (listId, task) => {
                set((state) => ({
                    taskLists: state.taskLists.map(list =>
                        list.id === listId
                            ? {
                                ...list,
                                tasks: list.tasks.filter(t => t.id !== task.id),
                                completedtasks: [
                                    { ...task, completed: Date.now() },
                                    ...list.completedtasks.filter(t => t.id !== task.id)
                                ]
                            }
                            : list
                    )
                }));
                get().syncTaskList(listId);
            },

            // ===== SCHEDULED TASK OPERATIONS =====

            scheduleTask: (listId, task, startTime) => {
                const cleanTask = { ...task };
                delete cleanTask.chosen;

                set((state) => ({
                    taskLists: state.taskLists.map(list => {
                        if (list.id !== listId) return list;

                        const originalTask = list.scheduledtasks.find(t => t.id === task.id) || {};
                        return {
                            ...list,
                            tasks: list.tasks.filter(t => t.id !== task.id),
                            scheduledtasks: [
                                ...list.scheduledtasks.filter(t => t.id !== task.id),
                                { ...originalTask, ...cleanTask, time: startTime }
                            ]
                        };
                    })
                }));
                get().syncTaskList(listId);
            },

            unscheduleTask: (listId, task) => {
                set((state) => ({
                    taskLists: state.taskLists.map(list => {
                        if (list.id !== listId) return list;

                        const originalTask = list.scheduledtasks.find(t => t.id === task.id);
                        if (!originalTask) return list;

                        const updatedTask = { ...originalTask, ...task };
                        delete updatedTask.time;
                        delete updatedTask.start;
                        delete updatedTask.end;
                        delete updatedTask.isAllDay;
                        delete updatedTask.checked;
                        delete updatedTask.chosen;
                        delete updatedTask.selected;
                        if (updatedTask.taskid) {
                            updatedTask.id = updatedTask.taskid;
                            delete updatedTask.taskid;
                        }

                        return {
                            ...list,
                            tasks: [updatedTask, ...list.tasks.filter(t => t.id !== updatedTask.id)],
                            scheduledtasks: list.scheduledtasks.filter(t => t.id !== task.id)
                        };
                    })
                }));
                get().syncTaskList(listId);
            },

            removeScheduledTask: (listId, taskId) => {
                set((state) => ({
                    taskLists: state.taskLists.map(list =>
                        list.id === listId
                            ? { ...list, scheduledtasks: list.scheduledtasks.filter(t => t.id !== taskId) }
                            : list
                    )
                }));
                get().syncTaskList(listId);
            },

            updateScheduledTask: (listId, taskId, updates) => {
                set((state) => ({
                    taskLists: state.taskLists.map(list =>
                        list.id === listId
                            ? {
                                ...list,
                                scheduledtasks: list.scheduledtasks.map(t =>
                                    t.id === taskId ? { ...t, ...updates } : t
                                )
                            }
                            : list
                    )
                }));
                get().syncTaskList(listId);
            },

            completeScheduledTask: (listId, task, completedDate = null) => {
                const date = completedDate || Date.now();
                set((state) => ({
                    taskLists: state.taskLists.map(list => {
                        if (list.id !== listId) return list;

                        const originalTask = list.scheduledtasks.find(t => t.id === task.id);
                        if (!originalTask) return list;

                        return {
                            ...list,
                            scheduledtasks: list.scheduledtasks.filter(t => t.id !== task.id),
                            completedtasks: [
                                { ...originalTask, ...task, completed: date },
                                ...list.completedtasks.filter(t => t.id !== task.id)
                            ]
                        };
                    })
                }));
                get().syncTaskList(listId);
            },

            // ===== COMPLETED TASK OPERATIONS =====

            removeCompletedTask: (listId, taskId) => {
                set((state) => ({
                    taskLists: state.taskLists.map(list =>
                        list.id === listId
                            ? { ...list, completedtasks: list.completedtasks.filter(t => t.id !== taskId) }
                            : list
                    )
                }));
                get().syncTaskList(listId);
            },

            uncompleteTask: (listId, task) => {
                const cleanTask = { ...task };
                delete cleanTask.completed;

                set((state) => ({
                    taskLists: state.taskLists.map(list =>
                        list.id === listId
                            ? {
                                ...list,
                                tasks: [...list.tasks, cleanTask],
                                completedtasks: list.completedtasks.filter(t => t.id !== task.id)
                            }
                            : list
                    )
                }));
                get().syncTaskList(listId);
            },

            uncompleteAndReschedule: (listId, task, scheduledDate = null) => {
                const date = scheduledDate || Date.now();
                set((state) => ({
                    taskLists: state.taskLists.map(list => {
                        if (list.id !== listId) return list;

                        const originalTask = list.completedtasks.find(t => t.id === task.id);
                        if (!originalTask) return list;

                        const cleanTask = { ...originalTask };
                        delete cleanTask.completed;

                        return {
                            ...list,
                            scheduledtasks: [
                                { ...cleanTask, ...task, time: date },
                                ...list.scheduledtasks.filter(t => t.id !== task.id)
                            ],
                            completedtasks: list.completedtasks.filter(t => t.id !== task.id)
                        };
                    })
                }));
                get().syncTaskList(listId);
            },

            // ===== INBOX OPERATIONS =====

            addTaskToInbox: async (task) => {
                const state = get();
                const inboxTitle = 'Inbox';
                const inbox = state.taskLists.find(l => l.title === inboxTitle);

                if (inbox) {
                    state.addTask(inbox.id, task);
                } else {
                    const newInbox = state.createTaskList(inboxTitle);
                    newInbox.tasks = [task];
                    set((s) => ({
                        taskLists: [newInbox, ...s.taskLists]
                    }));
                    // Create new inbox in Firestore immediately
                    const { userId } = get();
                    if (userId) {
                        await syncImmediate(
                            `task-create-${newInbox.id}`,
                            () => firestoreService.createTaskList(userId, newInbox),
                            () => cacheService.updateTaskList(newInbox.id, newInbox)
                        );
                    }
                }
            },

            addTaskToListOrInbox: (listId, task) => {
                const state = get();
                const listExists = state.taskLists.some(l => l.id === listId);
                if (listId && listExists) {
                    state.addTask(listId, task);
                } else {
                    state.addTaskToInbox(task);
                }
            },

            // ===== UTILITY =====

            convertTaskToEvent: (task, listId, color, textcolor, isDark) => ({
                ...task,
                listid: listId,
                type: 'task',
                color,
                textcolor,
                isDark,
                isDraggable: true,
            }),

            getListById: (listId) => get().taskLists.find(l => l.id === listId),

            clearAllTasks: async () => {
                const { userId, taskLists } = get();

                // Cancel all pending task syncs
                taskLists.forEach(list => cancelSync(`task-${list.id}`));

                set({ taskLists: [] });

                // Delete all from Firestore
                if (userId) {
                    await syncImmediate(
                        'task-clear-all',
                        async () => {
                            for (const taskList of taskLists) {
                                await firestoreService.deleteTaskList(userId, taskList.id);
                            }
                        },
                        () => cacheService.setTaskLists([])
                    );
                }
            },
        }),
        {
            name: 'grownups-tasks-storage',
        }
    )
);
